/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import * as drive from "../../../../api/drive";
import { formatBytes, formatDate } from "../format";

const TEXT_BUTTON_SX = { color: "text.color", textTransform: "none" };

/**
 * Past content this file's id has held. The live file is never listed here —
 * it's the row itself. Uploading a new version or restoring an old one both
 * snapshot whatever they're replacing first, so nothing in this list is ever
 * destroyed by using it.
 */
const VersionHistoryDialog = ({ open, file, onClose }) => {
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!file) return;
    try {
      setVersions(await drive.listFileVersions(file.id));
    } catch (err) {
      setError(err.message || "Could not load version history.");
      setVersions([]);
    }
  }, [file]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setVersions(null);
    load();
  }, [open, load]);

  const uploadNewVersion = async (fileList) => {
    const picked = fileList?.[0];
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await drive.uploadFileVersion(file.id, picked);
      await load();
    } catch (err) {
      setError(err.message || "Could not upload the new version.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async (versionId) => {
    setBusy(true);
    setError(null);
    try {
      await drive.restoreFileVersion(file.id, versionId);
      await load();
    } catch (err) {
      setError(err.message || "Could not restore that version.");
    } finally {
      setBusy(false);
    }
  };

  const download = async (versionId) => {
    const url = await drive.getVersionDownloadUrl(file.id, versionId);
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Version history — {file?.name}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button
          startIcon={<UploadFileOutlinedIcon />}
          sx={{ ...TEXT_BUTTON_SX, mb: 2 }}
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload new version
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            uploadNewVersion(e.target.files);
            e.target.value = "";
          }}
        />

        {versions === null ? (
          <Typography sx={{ fontSize: 13.5 }}>Loading…</Typography>
        ) : versions.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "secondaryText.color" }}>
            No earlier versions — this is the only content this file has ever had.
          </Typography>
        ) : (
          <List dense disablePadding>
            {versions.map((v) => (
              <ListItem
                key={v.id}
                disableGutters
                secondaryAction={
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Download this version">
                      <span>
                        <IconButton size="small" onClick={() => download(v.id)}>
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Restore this version">
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => restore(v.id)}>
                          <RestoreOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemText
                  primary={`Version ${v.version_number}`}
                  secondary={`${formatDate(v.created_at)} · ${formatBytes(v.size_bytes || 0)}`}
                  primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 11.5 }}
                />
                <Chip size="small" label={v.content_type || "unknown"} sx={{ fontSize: 10, mr: 6 }} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={TEXT_BUTTON_SX}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionHistoryDialog;
